/**
 * Vuex mutations for the "home" module.
 *
 * Synchronous state writers for feeds, the active post/reply, search, and
 * profile-tab collections. Business logic that needs async calls lives in
 * operations.js and commits these mutations with the results.
 */
import { groupPosts, groupPostsReply } from '@/helpers/groupPostsHelper';

import { getActiveTab } from '../../helpers/activeTabHelpers.js';

// If the user's own just-created post isn't in a fresh feed response yet
// (backend indexing/ranking lag), prepend it so it doesn't disappear from
// the feed between the optimistic insert and the server catching up. Once
// the server response does include it, stop re-injecting it.
function reinjectLastCreatedPost(state, freshPosts) {
  const pending = state.lastCreatedPost;
  if (!pending) return freshPosts;
  const alreadyPresent = freshPosts.some(p => p && p.id === pending.id);
  if (alreadyPresent) {
    state.lastCreatedPost = null;
    return freshPosts;
  }
  return [pending, ...freshPosts];
}

export default {
  // Global UI state
  setRouteLoading(state, isLoading) {
    state.isRouteLoading = !!isLoading;
  },
  setPosts(state, payload) {
    state.recommendedPosts = payload;
  },

  /** Replace the Recommended feed with `payload`, grouped and with any pending optimistic post re-injected. */
  setRecommendedPosts(state, payload) {
    if (!state || !payload) return;
    const groupedPosts = groupPosts(payload);
    // state.recommendedPosts = groupedPosts;
    state.recommendedPosts = reinjectLastCreatedPost(state, groupedPosts);
  },
  /** Replace the Following/For You feed with `payload`, grouped, id-normalized, and author-backfilled. */
  setFollowingPosts(state, { payload, postType }) {
    if (!state || !payload) return;
    // Use same simple grouping as For All feed to ensure consistent rendering
    // Provide id fallback if API returns _id
    const normalizedSource = payload.map(p => {
      if (p && !p.id && p._id) {
        return { ...p, id: p._id };
      }
      return p;
    });
    const grouped = groupPosts(normalizedSource);
    // Normalize minimal authorUser object to satisfy Post component v-if guards
    // state.followingPosts = grouped.map(p => {
    //   if (!p.authorUser && (p.authorUserId || p.authorUserID)) {
    //     const uid = p.authorUserId || p.authorUserID;
    //     const uname = p.username || p.handle || p.authorUsername || `user_${uid}`;
    //     return {
    //       ...p,
    //       authorUser: {
    //         id: uid,
    //         username: uname,
    //         rating: p.authorUserRating || 0,
    //         roles: Array.isArray(p.authorUserRoles) ? p.authorUserRoles : [],
    //         avatarFile: p.authorAvatar || p.avatarFile || { storageId: null },
    //       },
    //     };
    //   }
    //   return p;
    // });
    const normalized = grouped.map(p => {
      if (!p.authorUser && (p.authorUserId || p.authorUserID)) {
        const uid = p.authorUserId || p.authorUserID;
        const uname = p.username || p.handle || p.authorUsername || `user_${uid}`;
        return {
          ...p,
          authorUser: {
            id: uid,
            username: uname,
            rating: p.authorUserRating || 0,
            roles: Array.isArray(p.authorUserRoles) ? p.authorUserRoles : [],
            avatarFile: p.authorAvatar || p.avatarFile || { storageId: null },
          },
        };
      }
      return p;
    });
    state.followingPosts = reinjectLastCreatedPost(state, normalized);
    // Removed debug logging for production
  },
  // setPostsForyou(state, { collection, posts }) {
  //   if (state[collection]) {
  //     state[collection] = posts;
  //   }
  // },
  setReportBotId(state, id) {
    state.reportId = id;
  },
  setPostsSuggested(state, payload) {
    state.suggestedPosts = payload;
  },
  setPost(state, payload) {
    state.recommendedPost = payload;
  },

  setRepliePost(state, payload) {
    state.replies = payload;
  },

  // Add optimistic reply to local state immediately
  addOptimisticReply(state, reply) {
    // Create a temporary reply object with the expected structure
    const optimisticReply = {
      ...reply,
      id: `temp-${Date.now()}`, // Temporary ID
      isOptimistic: true, // Flag to identify optimistic updates
      createdAt: new Date().toISOString(),
    };
    // Add to the end of the replies array (newest first)
    state.replies = [...state.replies, optimisticReply];
  },

  // Remove optimistic reply when real reply is loaded
  removeOptimisticReply(state, tempId) {
    state.replies = state.replies.filter(reply => reply.id !== tempId);
  },

  /**
   * Apply the result of a create/update post action to `state.post` and to
   * whichever feed collection(s) the post belongs in (active tab's
   * collection, positive/negative reputation lists, replies thread, etc.).
   * `payload.type` is `'create'` or `'update'`.
   */
  postPost(state, payload) {
    state.post = payload.data;
    const activeTab = getActiveTab();
    if (activeTab && activeTab.collection) {
      if (!state[activeTab.collection]) {
        state[activeTab.collection] = [];
      }
      if (payload.type === 'create') {
        // New posts are unshifted to the front of each feed so they're
        // visible immediately, without waiting on a scroll to the bottom
        // of a 30-item page or on the backend's own feed ranking to settle.
        // (Previously these were push()'d to the end — kept below for
        // reference in case the ranking behavior needs to be restored.)
        // New posts are appended to the end of each feed, not unshifted to
        // the front, so the client never overrides the backend's ranking —
        // the next refetch/pagination will place it wherever the API ranks it.
        // if (activeTab.collection === 'positive') {
        //   state.positive = Array.isArray(state.positive) ? state.positive : [];
        //    state.positive.unshift(state.post);
        //   // state.positive.push(state.post);
        //   if (Array.isArray(state.negative) && state.negative.length > 0) {
        //     state.negative.push(state.post);
        //   }
        // }
        // if (activeTab.collection === 'negative') {
        //   state.negative = Array.isArray(state.negative) ? state.negative : [];
        //   state.negative.push(state.post);
        //   if (Array.isArray(state.positive) && state.positive.length > 0) {
        //     state.positive.push(state.post);
        //   }
        // }
        // if (activeTab.collection !== 'negative' && activeTab.collection !== 'positive') {
        //   if (
        //     payload.data.type !== 'reply' &&
        //     activeTab.collection !== 'userReplies' &&
        //     activeTab.collection !== 'userReposts'
        //   ) {
        //     state[activeTab.collection].push(state.post);
        //   }
        //   if (
        //     activeTab.collection === 'userReplies' ||
        //     activeTab.collection === 'userReposts' ||
        //     !activeTab.collection
        //   ) {
        //     state.onlyUserPosts.push(state.post);
        //   }
        //   if (Array.isArray(state.positive) && state.positive.length > 0) {
        //     state.positive.push(state.post);
        //   }
        //   if (Array.isArray(state.negative) && state.negative.length > 0) {
        //     state.negative.push(state.post);
        //   }
        // }
        if (activeTab.collection === 'positive') {
          state.positive = Array.isArray(state.positive) ? state.positive : [];
          state.positive.unshift(state.post);
          if (Array.isArray(state.negative) && state.negative.length > 0) {
            state.negative.unshift(state.post);
          }
        }
        if (activeTab.collection === 'negative') {
          state.negative = Array.isArray(state.negative) ? state.negative : [];
          state.negative.unshift(state.post);
          if (Array.isArray(state.positive) && state.positive.length > 0) {
            state.positive.unshift(state.post);
          }
        }
        if (activeTab.collection !== 'negative' && activeTab.collection !== 'positive') {
          if (
            payload.data.type !== 'reply' &&
            activeTab.collection !== 'userReplies' &&
            activeTab.collection !== 'userReposts'
          ) {
            state[activeTab.collection].unshift(state.post);
          }
          if (
            activeTab.collection === 'userReplies' ||
            activeTab.collection === 'userReposts' ||
            !activeTab.collection
          ) {
            state.onlyUserPosts.unshift(state.post);
          }
          if (Array.isArray(state.positive) && state.positive.length > 0) {
            state.positive.unshift(state.post);
          }
          if (Array.isArray(state.negative) && state.negative.length > 0) {
            state.negative.unshift(state.post);
          }
        }
      }
      // Remember it so a subsequent feed refetch (setRecommendedPosts /
      // setFollowingPosts) can re-inject it if the server response doesn't
      // include it yet (backend indexing/ranking lag).
      if (payload.type === 'create' && payload.data.type !== 'reply') {
        state.lastCreatedPost = state.post;
      }
    }
    if (payload.type === 'update') {
      // Update the edited post in every feed collection it may appear in,
      // regardless of which tab is active. Previously this only updated the
      // active-tab collection, so edits made while on the default home tab
      // (collection = '') or from another route (e.g. org) were invisible
      // in the home feed until a full page refresh.
      const updatedPost = state.post;
      const feedCollections = ['recommendedPosts', 'followingPosts', 'positive', 'negative', 'onlyUserPosts', 'userReplies'];
      feedCollections.forEach((col) => {
        if (!Array.isArray(state[col])) return;
        const idx = state[col].findIndex(p => p && p.id === updatedPost.id);
        if (idx !== -1) state[col].splice(idx, 1, updatedPost);
      });
    }
    if (payload.data.type === 'reply') {
      const index = state.replies.findIndex(reply => reply.id === payload.data.id);
      if (index !== -1) {
        state.replies.splice(index, 1, payload.data);
      }
    }
  },

  /** Remove `postId` from every feed/collection it might currently appear in. */
  removePost(state, postId) {
    const collections = [
      'recommendedPosts',
      'followingPosts',
      'onlyUserPosts',
      'replies',
      'userReplies',
      'userReposts',
      'allFollowPosts',
      'introPosts',
      'suggestedPosts',
    ];
    for (const key of collections) {
      if (Array.isArray(state[key]) && state[key].length) {
        state[key] = state[key].filter(item => item?.id !== postId);
      }
    }
  },

  /** Merge `data` into `state[collectionName]` by unique post id (used for paginated tab loads). */
  async setCollection(state, { collectionName, data, isReply }) {
    // Normalize inputs defensively
    const incoming = Array.isArray(data) ? data : (data && data.results && Array.isArray(data.results) ? data.results : []);
    if (!Array.isArray(state[collectionName])) {
      // Initialize target collection if missing
      state[collectionName] = [];
    }
    if (Array.isArray(state[collectionName])) {
      // Helper: merge arrays preserving order and ensuring unique ids
      const mergeUniqueById = (existing = [], incoming = []) => {
        const seen = new Set();
        const result = [];
        for (const item of existing) {
          const id = item && item.id;
          if (id == null || !seen.has(id)) {
            result.push(item);
            if (id != null) seen.add(id);
          }
        }
        for (const item of incoming) {
          const id = item && item.id;
          if (id == null || !seen.has(id)) {
            result.push(item);
            if (id != null) seen.add(id);
          }
        }
        return result;
      };

      if (collectionName === 'recommendedPosts' || collectionName === 'followingPosts') {
        // Modern social media feeds display posts individually, in the
        // order the API ranks them — existing page(s) first, then the newly
        // fetched page appended after, deduped by id.
        let processedPosts = incoming;
        if (isReply?.type === 'Reply') {
          processedPosts = groupPostsReply(processedPosts);
        }
        state[collectionName] = groupPosts(mergeUniqueById(state[collectionName], processedPosts));
        // state[collectionName] = mergeUniqueById(state[collectionName], processedPosts);
      } else {
       state[collectionName] = mergeUniqueById(state[collectionName], incoming);
      }
    } else {
      state[collectionName] = [...incoming];
    }
  },

  updateNow(state) {
    state.now = new Date();
  },
  setProfile(state, profile) {
    state.profile = profile;
  },
  setUserReplies(state, replies) {
    state.userReplies = replies;
  },
  setUserReposts(state, reposts) {
    state.userReposts = reposts;
  },
  setOnlyUserPosts(state, posts) {
    // OLD: hard replace with no reinjection — unlike setRecommendedPosts/
    // setFollowingPosts, a just-created post (e.g. a quote) that the
    // backend hadn't indexed yet by the time this fetch ran would vanish
    // from the profile's own Posts tab with nothing to bring it back.
    // state.onlyUserPosts = posts;
    state.onlyUserPosts = reinjectLastCreatedPost(state, posts);
  },

  setReputationCategories(state, categories) {
    state.reputationCategories = categories;
  },
  setStatusLoad(state, payload) {
    state.loadStatus = payload;
  },
  setIsReply(state, isReply) {
    state.isReply = isReply;
  },
  setUsernameData(state, data) {
    state.usernameData = data;
  },
  /** Remove `id` from every feed collection so a just-reported post disappears immediately app-wide. */
  removeReportedPost(state, id) {
    // Reported posts must disappear from whichever feed collection(s) they're
    // currently loaded in, not just recommendedPosts — otherwise a post
    // reported while on the default "Following"/unread tab stays visible
    // until a full page refresh re-fetches the feed from the server.
    const collections = [
      'recommendedPosts',
      'followingPosts',
      'unreadPosts',
      'onlyUserPosts',
      'replies',
      'userReplies',
      'userReposts',
      'allFollowPosts',
      'introPosts',
      'suggestedPosts',
    ];
    for (const key of collections) {
      if (Array.isArray(state[key]) && state[key].length) {
        state[key] = state[key].filter(post => post?.id !== id);
      }
    }
  },

  removeReportedPostWithCollection(state, { postId, collectionName }) {
    if (!state[collectionName]) {
      console.warn(`Collection "${collectionName}" does not exist.`);
      return;
    }
    state.collections[collectionName] = state.collections[collectionName].filter(post => post.id !== postId);
  },

  setReaction(state, payload) {
    state.replies = state.replies.map(post => (post.id === payload.id ? payload : post));
  },
  /** Overwrite `reactionSets` for `postId` (and its embedded `repostedPost`, if matched) across all feed collections and the detail view. */
  updatePostReactions(state, { postId, reactionSets }) {
    const safeReactionSets = Array.isArray(reactionSets) ? reactionSets : [];
    const apply = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((p, idx) => {
        if (!p) return;
        if (p.id === postId) {
          arr.splice(idx, 1, { ...p, reactionSets: safeReactionSets });
        } else if (p.repostedPost?.id === postId) {
          arr.splice(idx, 1, {
            ...p,
            repostedPost: { ...p.repostedPost, reactionSets: safeReactionSets },
          });
        }
      });
    };
    // Update current detailed post
    if (state.recommendedPost && state.recommendedPost.id === postId) {
      state.recommendedPost = { ...state.recommendedPost, reactionSets: safeReactionSets };
    } else if (state.recommendedPost?.repostedPost?.id === postId) {
      state.recommendedPost = {
        ...state.recommendedPost,
        repostedPost: { ...state.recommendedPost.repostedPost, reactionSets: safeReactionSets },
      };
    }
    // Update common collections
    apply(state.recommendedPosts);
    apply(state.followingPosts);
    apply(state.onlyUserPosts);
    apply(state.replies);
    apply(state.positive);
    apply(state.negative);
  },
  /** Attach a fresh integrated-replies preview (and total count) to `postId` wherever it appears in feeds or the detail view. */
  setIntegratedRepliesForPost(state, { postId, replies, total }) {
    const apply = (arr) => {
      if (!Array.isArray(arr)) return;
      const idx = arr.findIndex(p => p && p.id === postId);
      if (idx !== -1) {
        const post = arr[idx];
        const updated = {
          ...post,
          hasIntegratedReplies: true,
          integratedReplies: replies,
          totalRepliesCount: typeof total === 'number' ? total : (replies?.length || 0),
        };
        arr.splice(idx, 1, updated);
      }
    };
    apply(state.recommendedPosts);
    apply(state.followingPosts);
    if (state.recommendedPost && state.recommendedPost.id === postId) {
      state.recommendedPost = {
        ...state.recommendedPost,
        hasIntegratedReplies: true,
        integratedReplies: replies,
        totalRepliesCount: typeof total === 'number' ? total : (replies?.length || 0),
      };
    }
  },
  addReplyPostStatus(state, status) {
    state.replyPostStatus = status;
  },
  setTotalCountPosts(state, count) {
    state.totalCountPosts = count;
  },
  // setBlockOnReplies(state, blockedUsersIds) {
  //   state.replies.splice(
  //     0,
  //     state.replies.length,
  //     ...state.replies.filter((reply) => !blockedUsersIds.includes(reply.authorUserId))
  //   );
  // },
  setSearchText(state, text) {
    state.searchText = text;
  },
  setSearchResults(state, { users, posts, groups }) {
    state.searchResults.users = users;
    state.searchResults.posts = posts;
    state.searchResults.groups = groups || [];
  },
  setNoSearchResults(state, query) {
    state.noSearchResults = query;
  },
  clearSearchResults(state) {
    state.searchResults = { users: [], posts: [], groups: [] };
  },
  clearCollection(state, collectionName) {
    if (state[collectionName]) {
      state[collectionName] = [];
    }
  },
  setUnreadPosts(state, posts) {
    state.unreadPosts = posts;
  },
  setIntroPosts(state, posts) {
    state.introPosts = posts;
  },

  /** Record `postId` as deleted so stale UI references (e.g. cached feeds) can filter it out client-side. */
  trackDeletedPost(state, postId) {
    if (postId && !state.deletedPostIds.includes(postId)) {
      state.deletedPostIds.push(postId);
    }
  },
};
